import { UIHeading2, UIHeading4 } from '@cian/ui-kit';

import { type FC, useEffect, useRef } from 'react';
import { clsx } from 'clsx';

import { AdLabel } from 'shared/components/AdLabel';
import { type TAdfoxBannerCompleteSizes } from 'shared/types/adfox';
import { Ticker } from 'shared/components/Ticker';
import { type TSpecialProjectBannerAnimateContext } from 'shared/types/bannerContext';

import styles from './SpecialProjectAnimated.css';

export type TSpecialProjectAnimatedProps = {
  context: TSpecialProjectBannerAnimateContext;
  sizes: TAdfoxBannerCompleteSizes;
};

/**
 * Анимированный (parallax) баннер спецпроекта (`special_project_banner_animate`).
 *
 * Перенесён по аналогии с frontend-serp:
 * src/shared/components/AdfoxBanner/internal/components/templates/NewbuildingSpecialProjectAnimated/NewbuildingSpecialProjectAnimated.tsx
 * (бегущая строка `Marquee` заменена на локальный `Ticker`).
 */
export const SpecialProjectAnimated: FC<TSpecialProjectAnimatedProps> = ({ context, sizes }) => {
  const { title, text, url, buttonText, substrateNotNeeded, substrateLogoNotNeeded, disclaimer, logo, bgImage, label } =
    context;

  const containerRef = useRef<HTMLAnchorElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (backgroundRef.current) {
      backgroundRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
    }

    const updateParallax = (): void => {
      if (!containerRef.current || !backgroundRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementHeight = rect.height;
      const elementCenter = elementTop + elementHeight / 2;
      const viewportCenter = windowHeight / 2;

      if (elementTop < windowHeight && elementTop + elementHeight > 0) {
        const distanceFromCenter = (elementCenter - viewportCenter) / windowHeight;
        const parallaxOffset = distanceFromCenter * 80;
        backgroundRef.current.style.transform = `translate3d(0px, ${parallaxOffset}px, 0px)`;
      } else {
        backgroundRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
      }
    };

    const handleScroll = (): void => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(updateParallax);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateParallax();

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return (
    <a
      data-testid="special-project-animated"
      ref={containerRef}
      href={url}
      target="_blank"
      rel="nofollow noopener noreferrer"
      className={clsx(
        styles['container'],
        !Number(substrateNotNeeded) && styles['container--without-substrate'],
        !Number(substrateLogoNotNeeded) && styles['container--without-substratelogo'],
      )}
      style={sizes}
    >
      <div ref={backgroundRef} className={styles['background-layer']}>
        <img src={bgImage} alt="image" className={styles['background-image']} data-testid="background-image" />
      </div>
      <div className={styles['wrapper']}>
        <div className={styles['logo']}>
          <img className={styles['logo-image']} src={logo} alt={title} data-testid="logo" />
        </div>
        <div className={styles['content']}>
          <div className={styles['info']}>
            <div className={styles['title']} data-testid="title">
              {title}
            </div>
            <div className={styles['text']}>
              <UIHeading2 color="text-inverted-default">{text}</UIHeading2>
            </div>
          </div>

          <div className={styles['control']}>
            {!!label && (
              <div className={styles['control-label-wrapper']}>
                <AdLabel label={label} />
              </div>
            )}

            {!!buttonText && (
              <div className={styles['action-button-wrapper']}>
                <button type="button" className={styles['action-button']} data-testid="action-button">
                  <span className={styles['action-button-text']}>
                    <UIHeading4 color="text-inverted-default">{buttonText}</UIHeading4>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {disclaimer && (
        <div className={styles['note']}>
          <Ticker text={disclaimer} theme="light" />
        </div>
      )}
    </a>
  );
};
